import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "no-api-key-banner-dismissed";

interface SettingsData {
  anthropicConfigured: boolean;
  openaiConfigured: boolean;
}

export function NoApiKeyBanner() {
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );

  const { data: settings } = useQuery<SettingsData>({
    queryKey: ["/api/settings"],
    staleTime: 60_000,
  });

  if (dismissed) return null;

  const neitherConfigured =
    settings !== undefined &&
    !settings.anthropicConfigured &&
    !settings.openaiConfigured;

  if (!neitherConfigured) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleOpenSettings = () => {
    window.dispatchEvent(new CustomEvent("open-api-key-settings"));
  };

  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-6 text-sm text-amber-900">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
      <div className="flex-1 min-w-0">
        <p className="font-medium mb-0.5">Keine KI-API-Keys konfiguriert</p>
        <p className="text-amber-800 text-xs">
          Funktionen wie Foto-Analyse, Diktat und Dokumenten-Extraktion stehen
          erst nach der Einrichtung zur Verfügung.
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs border-amber-400 bg-white text-amber-900 hover:bg-amber-100 gap-1.5"
          onClick={handleOpenSettings}
        >
          <Settings className="w-3.5 h-3.5" />
          KI-Einstellungen
        </Button>
        <button
          type="button"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="text-amber-500 hover:text-amber-700 p-0.5 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
